// lib/configuration/NotifiFrontendConfiguration.ts
var newAptosConfig = (account, tenantId, env = "Development") => {
  return {
    tenantId,
    env,
    walletBlockchain: "APTOS",
    authenticationKey: account.publicKey,
    accountAddress: account.address
  };
};
var newSolanaConfig = (walletPublicKey, tenantId, env = "Development") => {
  return {
    tenantId,
    env,
    walletBlockchain: "SOLANA",
    walletPublicKey
  };
};
var envUrl = (env) => {
  switch (env) {
    case "Development":
      return "https://api.dev.notifi.network/gql";
    case "Local":
      return "https://localhost:5001/gql";
    case "Production":
      return "https://api.notifi.network/gql";
    case "Staging":
      return "https://api.stg.notifi.network/gql";
  }
};

// lib/utils/notNullOrEmpty.ts
var notNullOrEmpty = (item) => {
  return item !== void 0 && item !== null;
};

// lib/utils/packFilterOptions.ts
var packFilterOptions = (clientOptions) => {
  if (clientOptions === null) {
    return "{}";
  }
  return JSON.stringify(clientOptions);
};

// lib/utils/areIdsEqual.ts
var areIdsEqual = (ids, items) => {
  const idSet = new Set(ids);
  return items.length === idSet.size && items.every((it) => it !== void 0 && idSet.has(it.id));
};

// lib/utils/resolveRef.ts
var createRefResolver = (validator) => {
  return (name, valueOrRef, inputs) => {
    if (valueOrRef.type === "value") {
      return valueOrRef.value;
    } else {
      if (valueOrRef.ref === null) {
        throw new Error(`Invalid configuration: Ref ${name} is null`);
      }
      const runtimeInput = inputs[valueOrRef.ref];
      if (validator(runtimeInput)) {
        return runtimeInput;
      } else {
        throw new Error(`Invalid value provided for ${name}: ${runtimeInput}`);
      }
    }
  };
};
var resolveStringRef = createRefResolver(
  (item) => {
    return typeof item === "string";
  }
);

// lib/client/ensureSource.ts
var ensureDirectPushSource = async (service, _eventType, _inputs) => {
  const sourcesQuery = await service.getSources({});
  const sources = sourcesQuery.source;
  const source = sources == null ? void 0 : sources.find(
    (it) => it !== void 0 && it.type === "DIRECT_PUSH"
  );
  if (source === void 0) {
    throw new Error("Failed to identify direct push source");
  }
  return source;
};
var ensureBroadcastSource = async (service, eventType, inputs) => {
  const sourcesQuery = await service.getSources({});
  const sources = sourcesQuery.source;
  if (sources === void 0) {
    throw new Error("Failed to fetch sources");
  }
  const address = resolveStringRef(
    eventType.name,
    eventType.broadcastId,
    inputs
  );
  const existing = sources.find(
    (it) => (it == null ? void 0 : it.type) === "BROADCAST" && it.blockchainAddress === address
  );
  if (existing !== void 0) {
    return existing;
  }
  const createMutation = await service.createSource({
    type: "BROADCAST",
    blockchainAddress: address
  });
  const result = createMutation.createSource;
  if (result === void 0) {
    throw new Error("Failed to create source");
  }
  return result;
};
var ensurePriceChangeSources = async (service, eventType, _inputs) => {
  const sourcesQuery = await service.getSources({});
  const sources = sourcesQuery.source;
  if (sources === void 0) {
    throw new Error("Failed to fetch sources");
  }
  const results = [];
  const sourcesToCreate = new Set(eventType.tokenIds);
  sources.forEach((existing) => {
    if ((existing == null ? void 0 : existing.type) === "COIN_PRICE_CHANGES") {
      sourcesToCreate.delete(existing.blockchainAddress);
      results.push(existing);
    }
  });
  if (sourcesToCreate.size > 0) {
    let createSourcePromise = Promise.resolve();
    sourcesToCreate.forEach((tokenId) => {
      createSourcePromise = createSourcePromise.then(async () => {
        const result = await service.createSource({
          type: "COIN_PRICE_CHANGES",
          blockchainAddress: tokenId
        });
        const source = result.createSource;
        if (source !== void 0) {
          results.push(source);
        }
      });
    });
    await createSourcePromise;
  }
  return results;
};
var ensureSources = async (service, eventType, inputs) => {
  switch (eventType.type) {
    case "directPush": {
      const source = await ensureDirectPushSource(service, eventType, inputs);
      return [source];
    }
    case "broadcast": {
      const source = await ensureBroadcastSource(service, eventType, inputs);
      return [source];
    }
    case "priceChange": {
      const sources = await ensurePriceChangeSources(
        service,
        eventType,
        inputs
      );
      return sources;
    }
    case "label": {
      throw new Error("Unsupported event type");
    }
  }
};
var ensureSourceGroup = async (service, name, sourceIds) => {
  var _a;
  const sourceGroupsQuery = await service.getSourceGroups({});
  const existing = (_a = sourceGroupsQuery.sourceGroup) == null ? void 0 : _a.find(
    (it) => it !== void 0 && it.name === name
  );
  if (existing === void 0) {
    const createMutation = await service.createSourceGroup({
      name,
      sourceIds
    });
    const createResult = createMutation.createSourceGroup;
    if (createResult === void 0) {
      throw new Error("Failed to create source group");
    }
    return createResult;
  }
  if (areIdsEqual(sourceIds, existing.sources ?? [])) {
    return existing;
  }
  const updateMutation = await service.updateSourceGroup({
    id: existing.id,
    name,
    sourceIds
  });
  const updateResult = updateMutation.updateSourceGroup;
  if (updateResult === void 0) {
    throw new Error("Failed to update source group");
  }
  return updateResult;
};
var getDirectPushFilter = (source, eventType, inputs) => {
  var _a;
  const filter = (_a = source.applicableFilters) == null ? void 0 : _a.find(
    (it) => (it == null ? void 0 : it.filterType) === "DIRECT_TENANT_MESSAGES"
  );
  if (filter === void 0) {
    throw new Error("Failed to retrieve filter");
  }
  const type = resolveStringRef(eventType.name, eventType.directPushId, inputs);
  const filterOptions = {
    directMessageType: type
  };
  return {
    filter,
    filterOptions
  };
};
var getBroadcastFilter = (source, _eventType, _inputs) => {
  var _a;
  const filter = (_a = source.applicableFilters) == null ? void 0 : _a.find(
    (it) => (it == null ? void 0 : it.filterType) === "BROADCAST_MESSAGES"
  );
  if (filter === void 0) {
    throw new Error("Failed to retrieve filter");
  }
  return {
    filter,
    filterOptions: {}
  };
};
var getPriceChangeFilter = (sources, _eventType, _inputs) => {
  const filter = sources.flatMap((it) => it.applicableFilters ?? []).find((it) => (it == null ? void 0 : it.filterType) === "COIN_PRICE_CHANGE_EVENTS");
  if (filter === void 0) {
    throw new Error("Failed to retrieve filter");
  }
  return {
    filter,
    filterOptions: {}
  };
};
var ensureSourceAndFilters = async (service, eventType, inputs) => {
  const sources = await ensureSources(service, eventType, inputs);
  const sourceGroup = await ensureSourceGroup(
    service,
    eventType.name,
    sources.map((it) => it.id)
  );
  switch (eventType.type) {
    case "directPush": {
      const { filter, filterOptions } = getDirectPushFilter(
        sources[0],
        eventType,
        inputs
      );
      return {
        sourceGroup,
        filter,
        filterOptions
      };
    }
    case "broadcast": {
      const { filter, filterOptions } = getBroadcastFilter(
        sources[0],
        eventType,
        inputs
      );
      return {
        sourceGroup,
        filter,
        filterOptions
      };
    }
    case "priceChange": {
      const { filter, filterOptions } = getPriceChangeFilter(
        sources,
        eventType,
        inputs
      );
      return {
        sourceGroup,
        filter,
        filterOptions
      };
    }
    case "label": {
      throw new Error("Unsupported event type");
    }
  }
};

// lib/client/ensureTarget.ts
var ensureTarget = (create, fetch, identify, valueTransform) => {
  return async (service, value) => {
    if (value === void 0) {
      return void 0;
    }
    const existing = await fetch(service);
    const transformedValue = valueTransform !== void 0 ? valueTransform(value) : value;
    const found = existing == null ? void 0 : existing.find((it) => identify(it) === transformedValue);
    if (found !== void 0) {
      return found.id;
    }
    const created = await create(service, transformedValue);
    return created.id;
  };
};
var ensureEmail = ensureTarget(
  async (service, value) => {
    const mutation = await service.createEmailTarget({
      name: value.toLowerCase(),
      value: value.toLowerCase()
    });
    const result = mutation.createEmailTarget;
    if (result === void 0) {
      throw new Error("Failed to create email target");
    }
    return result;
  },
  async (service) => {
    const query = await service.getEmailTargets({});
    return query.emailTarget;
  },
  (arg) => {
    var _a;
    return (_a = arg == null ? void 0 : arg.emailAddress) == null ? void 0 : _a.toLowerCase();
  },
  (value) => value.toLowerCase()
);
var ensureSms = ensureTarget(
  async (service, value) => {
    const mutation = await service.createSmsTarget({
      name: value,
      value
    });
    const result = mutation.createSmsTarget;
    if (result === void 0) {
      throw new Error("Failed to create sms target");
    }
    return result;
  },
  async (service) => {
    const query = await service.getSmsTargets({});
    return query.smsTarget;
  },
  (arg) => arg == null ? void 0 : arg.phoneNumber
);
var ensureTelegram = ensureTarget(
  async (service, value) => {
    const mutation = await service.createTelegramTarget({
      name: value.toLowerCase(),
      value: value.toLowerCase()
    });
    const result = mutation.createTelegramTarget;
    if (result === void 0) {
      throw new Error("Failed to create telegramTarget");
    }
    return result;
  },
  async (service) => {
    const query = await service.getTelegramTargets({});
    return query.telegramTarget;
  },
  (arg) => {
    var _a;
    return (_a = arg == null ? void 0 : arg.telegramId) == null ? void 0 : _a.toLowerCase();
  },
  (value) => value.toLowerCase()
);
var ensureWebhook = async (service, params) => {
  if (params === void 0) {
    return void 0;
  }
  const query = await service.getWebhookTargets({});
  const existing = query.webhookTarget;
  const found = existing == null ? void 0 : existing.find(
    (it) => (it == null ? void 0 : it.url.toLowerCase()) === params.url.toLowerCase() && (it == null ? void 0 : it.format) === params.format
  );
  if (found !== void 0) {
    return found.id;
  }
  const mutation = await service.createWebhookTarget({
    ...params,
    name: params.url.toLowerCase(),
    url: params.url.toLowerCase()
  });
  const created = mutation.createWebhookTarget;
  if (created === void 0) {
    throw new Error("Failed to create webhook target");
  }
  return created.id;
};

// lib/client/NotifiFrontendClient.ts
var SIGNING_MESSAGE = `Sign in with Notifi 

    No password needed or gas is needed. 

    Clicking \u201CApprove\u201D only means you have proved this wallet is owned by you! 

    This request will not trigger any transaction or cost any gas fees. 

    Use of our website and service is subject to our terms of service and privacy policy. 
 
 'Nonce:' `;
var NotifiFrontendClient = class {
  constructor(_configuration, _service, _storage) {
    this._configuration = _configuration;
    this._service = _service;
    this._storage = _storage;
    this._clientRandomUuid = null;
  }
  async initialize() {
    const [storedAuthorization, roles] = await Promise.all([
      this._storage.getAuthorization(),
      this._storage.getRoles()
    ]);
    let authorization = storedAuthorization;
    if (authorization === null) {
      this._service.setJwt(void 0);
      return {
        status: "loggedOut"
      };
    }
    const expiryDate = new Date(authorization.expiry);
    const now = new Date();
    if (expiryDate <= now) {
      this._service.setJwt(void 0);
      return {
        status: "expired",
        authorization
      };
    }
    const refreshTime = new Date();
    refreshTime.setDate(now.getDate() + 7);
    if (expiryDate < refreshTime) {
      try {
        const refreshMutation = await this._service.refreshAuthorization({});
        const newAuthorization = refreshMutation.refreshAuthorization;
        if (newAuthorization !== void 0) {
          this._storage.setAuthorization(newAuthorization);
          authorization = newAuthorization;
        }
      } catch (e) {
        console.log("Failed to refresh Notifi token", e);
      }
    }
    this._service.setJwt(authorization.token);
    return {
      status: "authenticated",
      authorization,
      roles: roles ?? []
    };
  }
  async logOut() {
    await Promise.all([
      this._storage.setAuthorization(null),
      this._storage.setRoles(null),
      this._service.logOut()
    ]);
    return {
      status: "loggedOut"
    };
  }
  async logIn(signMessageParams) {
    const timestamp = Math.round(Date.now() / 1e3);
    const signature = await this._signMessage({
      signMessageParams,
      timestamp
    });
    const { tenantId, walletBlockchain } = this._configuration;
    let loginResult = void 0;
    switch (walletBlockchain) {
      case "SOLANA": {
        const result = await this._service.logInFromDapp({
          walletBlockchain,
          walletPublicKey: this._configuration.walletPublicKey,
          dappAddress: tenantId,
          timestamp,
          signature
        });
        loginResult = result.logInFromDapp;
        break;
      }
      case "APTOS": {
        const result = await this._service.logInFromDapp({
          walletBlockchain,
          walletPublicKey: this._configuration.authenticationKey,
          accountId: this._configuration.accountAddress,
          dappAddress: tenantId,
          timestamp,
          signature
        });
        loginResult = result.logInFromDapp;
        break;
      }
    }
    if (loginResult === void 0) {
      return Promise.reject("Failed to login");
    }
    await this._handleLogInResult(loginResult);
    return loginResult;
  }
  async _signMessage({
    signMessageParams,
    timestamp
  }) {
    switch (signMessageParams.walletBlockchain) {
      case "SOLANA": {
        if (this._configuration.walletBlockchain !== "SOLANA") {
          throw new Error(
            "Sign message params and configuration must have the same blockchain"
          );
        }
        const { walletPublicKey, tenantId } = this._configuration;
        const messageBuffer = new TextEncoder().encode(
          `${SIGNING_MESSAGE}${walletPublicKey}${tenantId}${timestamp.toString()}`
        );
        const signedBuffer = await signMessageParams.signMessage(messageBuffer);
        const signature = Buffer.from(signedBuffer).toString("base64");
        return signature;
      }
      case "APTOS": {
        if (this._configuration.walletBlockchain !== "APTOS") {
          throw new Error(
            "Sign message params and configuration must have the same blockchain"
          );
        }
        const signature = await signMessageParams.signMessage(
          SIGNING_MESSAGE,
          timestamp
        );
        return signature;
      }
    }
  }
  async _handleLogInResult(user) {
    const authorization = user == null ? void 0 : user.authorization;
    const saveAuthorizationPromise = authorization !== void 0 ? this._storage.setAuthorization(authorization) : Promise.resolve();
    const roles = user == null ? void 0 : user.roles;
    const saveRolesPromise = roles !== void 0 ? this._storage.setRoles(roles.filter(notNullOrEmpty)) : Promise.resolve();
    await Promise.all([saveAuthorizationPromise, saveRolesPromise]);
  }
  async beginLoginViaTransaction({
    walletBlockchain,
    walletAddress
  }) {
    const { tenantId } = this._configuration;
    const result = await this._service.beginLogInByTransaction({
      walletAddress,
      walletBlockchain,
      dappAddress: tenantId
    });
    const nonce = result.beginLogInByTransaction.nonce;
    if (nonce === null) {
      throw new Error("Failed to begin login process");
    }
    const ruuid = crypto.randomUUID();
    this._clientRandomUuid = ruuid;
    const encoder = new TextEncoder();
    const data = encoder.encode(nonce + ruuid);
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const logValue = "Notifi Auth: 0x" + hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
    return { nonce: logValue };
  }
  async completeLoginViaTransaction({
    walletBlockchain,
    walletAddress,
    transactionSignature
  }) {
    const { tenantId } = this._configuration;
    const clientRandomUuid = this._clientRandomUuid;
    this._clientRandomUuid = null;
    if (clientRandomUuid === null) {
      throw new Error(
        "BeginLoginViaTransaction is required to be called first"
      );
    }
    const result = await this._service.completeLogInByTransaction({
      walletAddress,
      walletBlockchain,
      dappAddress: tenantId,
      randomUuid: clientRandomUuid,
      transactionSignature
    });
    await this._handleLogInResult(result.completeLogInByTransaction);
    return result;
  }
  async getTargetGroups() {
    var _a;
    const query = await this._service.getTargetGroups({});
    const results = ((_a = query.targetGroup) == null ? void 0 : _a.filter(notNullOrEmpty)) ?? [];
    return results;
  }
  async ensureTargetGroup({
    name,
    emailAddress,
    phoneNumber,
    telegramId,
    webhook
  }) {
    var _a;
    const [
      targetGroupsQuery,
      emailTargetId,
      smsTargetId,
      telegramTargetId,
      webhookTargetId
    ] = await Promise.all([
      this._service.getTargetGroups({}),
      ensureEmail(this._service, emailAddress),
      ensureSms(this._service, phoneNumber),
      ensureTelegram(this._service, telegramId),
      ensureWebhook(this._service, webhook)
    ]);
    const emailTargetIds = emailTargetId === void 0 ? [] : [emailTargetId];
    const smsTargetIds = smsTargetId === void 0 ? [] : [smsTargetId];
    const telegramTargetIds = telegramTargetId === void 0 ? [] : [telegramTargetId];
    const webhookTargetIds = webhookTargetId === void 0 ? [] : [webhookTargetId];
    const existing = (_a = targetGroupsQuery.targetGroup) == null ? void 0 : _a.find(
      (it) => (it == null ? void 0 : it.name) === name
    );
    if (existing !== void 0) {
      return this._updateTargetGroup({
        existing,
        emailTargetIds,
        smsTargetIds,
        telegramTargetIds,
        webhookTargetIds
      });
    }
    const createMutation = await this._service.createTargetGroup({
      name,
      emailTargetIds,
      smsTargetIds,
      telegramTargetIds,
      webhookTargetIds
    });
    if (createMutation.createTargetGroup === void 0) {
      throw new Error("Failed to create target group");
    }
    return createMutation.createTargetGroup;
  }
  async _updateTargetGroup({
    existing,
    emailTargetIds,
    smsTargetIds,
    telegramTargetIds,
    webhookTargetIds
  }) {
    if (areIdsEqual(emailTargetIds, existing.emailTargets ?? []) && areIdsEqual(smsTargetIds, existing.smsTargets ?? []) && areIdsEqual(telegramTargetIds, existing.telegramTargets ?? []) && areIdsEqual(webhookTargetIds, existing.webhookTargets ?? [])) {
      return existing;
    }
    const updateMutation = await this._service.updateTargetGroup({
      id: existing.id,
      name: existing.name ?? existing.id,
      emailTargetIds,
      smsTargetIds,
      telegramTargetIds,
      webhookTargetIds
    });
    const updated = updateMutation.updateTargetGroup;
    if (updated === void 0) {
      throw new Error("Failed to update target group");
    }
    return updated;
  }
  async getSourceGroups() {
    var _a;
    const query = await this._service.getSourceGroups({});
    const results = ((_a = query.sourceGroup) == null ? void 0 : _a.filter(notNullOrEmpty)) ?? [];
    return results;
  }
  async getAlerts() {
    var _a;
    const query = await this._service.getAlerts({});
    return ((_a = query.alert) == null ? void 0 : _a.filter(notNullOrEmpty)) ?? [];
  }
  async ensureAlert({
    eventType,
    inputs
  }) {
    var _a, _b;
    const [alertsQuery, targetGroupsQuery, sourceAndFilters] = await Promise.all([
      this._service.getAlerts({}),
      this._service.getTargetGroups({}),
      ensureSourceAndFilters(this._service, eventType, inputs)
    ]);
    const targetGroup = (_a = targetGroupsQuery.targetGroup) == null ? void 0 : _a.find(
      (it) => (it == null ? void 0 : it.name) === "Default"
    );
    if (targetGroup === void 0) {
      throw new Error("Default target group does not exist");
    }
    const { sourceGroup, filter, filterOptions } = sourceAndFilters;
    const packedOptions = packFilterOptions(filterOptions);
    const existing = (_b = alertsQuery.alert) == null ? void 0 : _b.find(
      (it) => it !== void 0 && it.name === eventType.name
    );
    if (existing !== void 0) {
      if (existing.sourceGroup.id === sourceGroup.id && existing.targetGroup.id === targetGroup.id && existing.filter.id === filter.id && existing.filterOptions === packedOptions) {
        return existing;
      }
      await this.deleteAlert({
        id: existing.id
      });
    }
    const mutation = await this._service.createAlert({
      name: eventType.name,
      sourceGroupId: sourceGroup.id,
      filterId: filter.id,
      targetGroupId: targetGroup.id,
      filterOptions: packedOptions,
      groupName: "managed"
    });
    const created = mutation.createAlert;
    if (created === void 0) {
      throw new Error("Failed to create alert");
    }
    return created;
  }
  async deleteAlert({
    id
  }) {
    var _a;
    const mutation = await this._service.deleteAlert({ id });
    const result = (_a = mutation.deleteAlert) == null ? void 0 : _a.id;
    if (result === void 0) {
      throw new Error("Failed to delete alert");
    }
  }
  async getNotificationHistory(variables) {
    var _a, _b;
    const query = await this._service.getNotificationHistory(variables);
    const nodes = (_a = query.notificationHistory) == null ? void 0 : _a.nodes;
    const pageInfo = (_b = query.notificationHistory) == null ? void 0 : _b.pageInfo;
    if (nodes === void 0 || pageInfo === void 0) {
      throw new Error("Failed to fetch notification history");
    }
    return { pageInfo, nodes };
  }
  async fetchSubscriptionCard({
    cardId
  }) {
    const query = await this._service.findTenantConfig({
      input: {
        id: cardId,
        tenant: this._configuration.tenantId,
        type: "SUBSCRIPTION_CARD"
      }
    });
    const result = query.findTenantConfig;
    if (result === void 0) {
      throw new Error("Failed to find tenant config");
    }
    const value = result.dataJson;
    if (value === void 0) {
      throw new Error("Invalid config data");
    }
    const obj = JSON.parse(value);
    let card = void 0;
    switch (obj.version) {
      case "v1": {
        card = obj;
        break;
      }
    }
    if (card === void 0) {
      throw new Error("Unsupported config format");
    }
    return card;
  }
  async sendEmailTargetVerification({
    targetId
  }) {
    var _a;
    const emailTarget = await this._service.sendEmailTargetVerificationRequest({
      targetId
    });
    const id = (_a = emailTarget.sendEmailTargetVerificationRequest) == null ? void 0 : _a.id;
    if (id === void 0) {
      throw new Error(`Unknown error requesting verification`);
    }
    return id;
  }
};

// lib/client/clientFactory.ts
import { NotifiService } from "@notifi-network/notifi-graphql";
import { GraphQLClient } from "graphql-request";

// lib/storage/InMemoryStorageDriver.ts
var getEnvPrefix = (env) => {
  switch (env) {
    case "Production":
      return "notifi-jwt";
    case "Development":
      return "notifi-jwt:dev";
    case "Staging":
      return "notifi-jwt:stg";
    case "Local":
      return "notifi-jwt:local";
  }
};
var createInMemoryStorageDriver = (config) => {
  let keyPrefix = `${getEnvPrefix(config.env)}:${config.tenantId}`;
  switch (config.walletBlockchain) {
    case "SOLANA": {
      keyPrefix += `:${config.walletPublicKey}`;
      break;
    }
    case "APTOS": {
      keyPrefix += `:${config.accountAddress}:${config.authenticationKey}`;
      break;
    }
  }
  const storageBackend = {};
  const storageDriver = {
    get: (key) => {
      const newKey = `${keyPrefix}:${key}`;
      let result = null;
      if (newKey in storageBackend) {
        const json = storageBackend[newKey];
        result = JSON.parse(json);
      }
      return Promise.resolve(result);
    },
    set: (key, newValue) => {
      const newKey = `${keyPrefix}:${key}`;
      if (newValue === null) {
        delete storageBackend[newKey];
      } else {
        storageBackend[newKey] = JSON.stringify(newValue);
      }
      return Promise.resolve();
    },
    has: (key) => {
      const newKey = `${keyPrefix}:${key}`;
      return Promise.resolve(newKey in storageBackend);
    }
  };
  return storageDriver;
};

// lib/storage/NotifiFrontendStorage.ts
var KEY_AUTHORIZATION = "authorization";
var KEY_ROLES = "roles";
var NotifiFrontendStorage = class {
  constructor(_driver) {
    this._driver = _driver;
  }
  getAuthorization() {
    return this._driver.get(KEY_AUTHORIZATION);
  }
  setAuthorization(newValue) {
    return this._driver.set(KEY_AUTHORIZATION, newValue);
  }
  hasAuthorization() {
    return this._driver.has(KEY_AUTHORIZATION);
  }
  getRoles() {
    return this._driver.get(KEY_ROLES);
  }
  setRoles(newValue) {
    return this._driver.set(KEY_ROLES, newValue);
  }
  hasRoles() {
    return this._driver.has(KEY_ROLES);
  }
};

// lib/storage/LocalForageStorageDriver.ts
import localforage from "localforage";
localforage.config({
  name: "notifi"
});
var getEnvPrefix2 = (env) => {
  switch (env) {
    case "Production":
      return "notifi-jwt";
    case "Development":
      return "notifi-jwt:dev";
    case "Staging":
      return "notifi-jwt:stg";
    case "Local":
      return "notifi-jwt:local";
  }
};
var createLocalForageStorageDriver = (config) => {
  let keyPrefix = `${getEnvPrefix2(config.env)}:${config.tenantId}`;
  switch (config.walletBlockchain) {
    case "SOLANA": {
      keyPrefix += `:${config.walletPublicKey}`;
      break;
    }
    case "APTOS": {
      keyPrefix += `:${config.accountAddress}:${config.authenticationKey}`;
      break;
    }
  }
  const storageDriver = {
    get: async (key) => {
      const item = await localforage.getItem(`${keyPrefix}:${key}`);
      return item;
    },
    set: async (key, newValue) => {
      await localforage.setItem(`${keyPrefix}:${key}`, newValue);
    },
    has: async (key) => {
      const keys = await localforage.keys();
      return keys.indexOf(`${keyPrefix}:${key}`) >= 0;
    }
  };
  return storageDriver;
};

// lib/client/clientFactory.ts
var newNotifiStorage = (config) => {
  const driver = createLocalForageStorageDriver(config);
  return new NotifiFrontendStorage(driver);
};
var newNotifiService = (config) => {
  const url = envUrl(config.env);
  const client = new GraphQLClient(url);
  return new NotifiService(client);
};
var newAptosClient = (config) => {
  const service = newNotifiService(config);
  const storage = newNotifiStorage(config);
  return new NotifiFrontendClient(config, service, storage);
};
var newSolanaClient = (config) => {
  const service = newNotifiService(config);
  const storage = newNotifiStorage(config);
  return new NotifiFrontendClient(config, service, storage);
};
export {
  NotifiFrontendClient,
  NotifiFrontendStorage,
  SIGNING_MESSAGE,
  createInMemoryStorageDriver,
  createLocalForageStorageDriver,
  ensureEmail,
  ensureSms,
  ensureSourceAndFilters,
  ensureTarget,
  ensureTelegram,
  ensureWebhook,
  envUrl,
  newAptosClient,
  newAptosConfig,
  newNotifiService,
  newNotifiStorage,
  newSolanaClient,
  newSolanaConfig,
  notNullOrEmpty,
  packFilterOptions
};
